import type { VisitorsFieldMode, VisitorsModuleConfig } from '@/types/space-wizard'
import type { VisitorInvitation } from '@/types/visitors'

export const MAX_INSTRUCTIONS = 500

export const apiMessage = (err: any, fallback: string): string =>
  err?.response?.data?.message || err?.message || fallback

/** `{ param, message }` pairs the API sends with a 400, as `param -> message` (last segment of the param). */
export const apiFieldErrors = (err: any): Record<string, string> => {
  const list: Array<{ param?: string; message?: string }> = Array.isArray(err?.response?.data?.errors) ? err.response.data.errors : []
  const out: Record<string, string> = {}
  for (const e of list) if (e.param && e.message) out[e.param.split('.').pop() as string] = e.message
  return out
}

/* ------------------------------------------------------------------ setup -- */

/** What a Visitors space is born with (F-83 §4.2). The Presence link has no default: it is the one required choice. */
export const defaultVisitorsConfig = (): VisitorsModuleConfig => ({
  attendanceSpaceId: null,
  whoCanInvite: 'members',
  inviterGroupId: null,
  fields: { company: 'optional', email: 'optional', phone: 'optional' },
  notifyHostOnRegister: true,
  emailGuest: 'off',
  arrivalInstructions: '',
  maxExpectedPerHostPerDay: null
})

/** The defaults under what the space stores, so a space saved before a key existed shows it filled. */
export const mergeVisitorsConfig = (stored?: VisitorsModuleConfig | null): VisitorsModuleConfig => {
  const base = defaultVisitorsConfig()
  return {
    ...base,
    ...(stored ?? {}),
    fields: { ...base.fields, ...(stored?.fields ?? {}) },
    arrivalInstructions: stored?.arrivalInstructions ?? ''
  }
}

export interface VisitorsConfigErrors {
  attendanceSpaceId?: string
  inviterGroupId?: string
  arrivalInstructions?: string
  maxExpectedPerHostPerDay?: string
}

/** Same bounds as the API (instructions up to 500, limit 1 to 1000); the server stays the judge. */
export const validateVisitorsConfig = (
  config: VisitorsModuleConfig,
  t: (key: string, vars?: Record<string, unknown>) => string
): VisitorsConfigErrors | null => {
  const errors: VisitorsConfigErrors = {}
  if (!config.attendanceSpaceId) errors.attendanceSpaceId = t('visitors:config.attendance.required')
  if (config.whoCanInvite === 'group' && !config.inviterGroupId) errors.inviterGroupId = t('visitors:config.group.required')
  if ((config.arrivalInstructions ?? '').length > MAX_INSTRUCTIONS) {
    errors.arrivalInstructions = t('visitors:config.instructions.tooLong', { max: MAX_INSTRUCTIONS })
  }
  const max = config.maxExpectedPerHostPerDay
  if (max !== null && max !== undefined && !(Number.isInteger(max) && max >= 1 && max <= 1000)) {
    errors.maxExpectedPerHostPerDay = t('visitors:config.limit.invalid')
  }
  return Object.keys(errors).length ? errors : null
}

/** The slice that goes to the API; the rest of the stored object is merged by the caller. */
export const toVisitorsInput = (config: VisitorsModuleConfig): VisitorsModuleConfig => {
  const merged = mergeVisitorsConfig(config)
  return {
    attendanceSpaceId: merged.attendanceSpaceId,
    whoCanInvite: merged.whoCanInvite,
    inviterGroupId: merged.whoCanInvite === 'group' ? merged.inviterGroupId : null,
    fields: merged.fields,
    notifyHostOnRegister: merged.notifyHostOnRegister,
    emailGuest: merged.emailGuest,
    arrivalInstructions: (merged.arrivalInstructions ?? '').trim(),
    maxExpectedPerHostPerDay: merged.maxExpectedPerHostPerDay ?? null
  }
}

export const visitorsConfigApiErrors = (err: any): VisitorsConfigErrors | null => {
  const fields = apiFieldErrors(err)
  const errors: VisitorsConfigErrors = {
    attendanceSpaceId: fields.attendanceSpaceId,
    inviterGroupId: fields.inviterGroupId,
    arrivalInstructions: fields.arrivalInstructions,
    maxExpectedPerHostPerDay: fields.maxExpectedPerHostPerDay
  }
  return Object.values(errors).some(Boolean) ? errors : null
}

/** The config the page reads from the space, with defaults. */
export const spaceVisitorsConfig = (space?: { moduleConfig?: any } | null): VisitorsModuleConfig =>
  mergeVisitorsConfig(space?.moduleConfig?.visitors)

export type { VisitorsFieldMode }

/* ------------------------------------------------------------------ dates -- */

const safeZone = (timeZone?: string): string | undefined => {
  if (!timeZone) return undefined
  try {
    new Intl.DateTimeFormat('en', { timeZone })
    return timeZone
  } catch {
    return undefined
  }
}

/** `yyyy-mm-dd` of today on the community's calendar. */
export const todayKey = (timeZone?: string): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: safeZone(timeZone) }).format(new Date())

/** A `yyyy-mm-dd` calendar day, written for the reader. The day carries no zone, so it is read as UTC noon. */
export const formatDateKey = (key: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' }).format(new Date(`${key}T12:00:00Z`))

/** `HH:mm` of an instant on the community's clock. */
export const formatInstant = (iso: string | null | undefined, locale: string, timeZone?: string): string =>
  iso ? new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone: safeZone(timeZone) }).format(new Date(iso)) : ''

export const windowLabel = (inv: Pick<VisitorInvitation, 'startTime' | 'endTime'>): string =>
  inv.endTime ? `${inv.startTime} – ${inv.endTime}` : inv.startTime

export const hostName = (inv: VisitorInvitation): string =>
  inv.host?.displayName || inv.host?.name || ''
